import React from 'react';
import ReactDOMServer from 'react-dom/server';
import DeliveryReceipt2, { DeliveryReceiptSettings, prepareOrderForDeliveryReceipt } from '../components/order/delivery-receipt-2';
import { escapeHtml, openPrintWindow, writeAndPrint } from './printWindow';

function buildDeliveryReceiptPageHtml(order: any, settings?: DeliveryReceiptSettings): string {
  const prepared = prepareOrderForDeliveryReceipt(order, settings);
  return ReactDOMServer.renderToStaticMarkup(
    React.createElement(
      'div',
      { className: 'batch-receipt-page page-order' },
      React.createElement(DeliveryReceipt2, { order: prepared })
    )
  );
}

async function fetchDeliveryReceiptSettings(apiUrl: string): Promise<DeliveryReceiptSettings | undefined> {
  try {
    const res = await fetch(apiUrl + 'settings');
    if (!res.ok) return undefined;
    const resJson: any = await res.json();
    let freeDeliveryMinAmount: number | undefined;
    let deliveryPrice: number | undefined;
    let deliveryBaseWeightKg: number | undefined;
    let deliveryPricePerBase: number | undefined;
    for (const item of resJson.data || []) {
      if (item.key === 'FREE_DELIVERY_MIN') freeDeliveryMinAmount = Number(item.value);
      else if (item.key === 'DELIVERY_PRICE') deliveryPrice = Number(item.value);
      else if (item.key === 'DELIVERY_BASE_WEIGHT_KG') deliveryBaseWeightKg = Number(item.value);
      else if (item.key === 'DELIVERY_PRICE_PER_BASE') deliveryPricePerBase = Number(item.value);
    }
    return freeDeliveryMinAmount !== undefined && deliveryPrice !== undefined
      && deliveryBaseWeightKg !== undefined && deliveryPricePerBase !== undefined
      ? { freeDeliveryMinAmount, deliveryPrice, deliveryBaseWeightKg, deliveryPricePerBase }
      : undefined;
  } catch {
    return undefined;
  }
}

function aggregateProductRows(orders: any[]): { name: string; count: number }[] {
  const map = new Map<string, { name: string; count: number }>();
  for (const order of orders) {
    for (const delivery of order.deliveries || []) {
      for (const item of delivery.items || []) {
        const pid = String(item.product?.id ?? '');
        const name = item.product?.name ? String(item.product.name) : 'Бараа';
        const key = pid ? `id:${pid}` : `n:${name}`;
        const c = Number(item.count) || 0;
        const prev = map.get(key);
        if (prev) prev.count += c;
        else map.set(key, { name, count: c });
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'mn'));
}

function totalPieces(rows: { count: number }[]): number {
  return rows.reduce((sum, r) => sum + r.count, 0);
}

export type PrintUnprintedDeliveryReceiptBatchParams = {
  apiUrl: string;
  token: string;
  store: number;
  /** Passed to API as startDate — order.createdAt lower bound */
  orderStartDate: string;
  /** Passed to API as endDate — order.createdAt upper bound */
  orderEndDate: string;
  /** Optional — passed to API as deliveryStartDate */
  deliveryStartDate?: string;
  /** Optional — passed to API as deliveryEndDate */
  deliveryEndDate?: string;
};

export type PrintUnprintedDeliveryReceiptBatchResult =
  | { ok: true; printedCount: number }
  | { ok: false; error: string };

/**
 * Fetches confirmed unprinted (receiptCount 0) orders for the store and date filters,
 * opens print preview, then increments receipt counts after the print dialog closes.
 */
export async function printUnprintedDeliveryReceiptBatch(
  params: PrintUnprintedDeliveryReceiptBatchParams
): Promise<PrintUnprintedDeliveryReceiptBatchResult> {
  const { apiUrl, token, store, orderStartDate, orderEndDate, deliveryStartDate, deliveryEndDate } = params;

  // Must open synchronously, before any `await` (see openPrintWindow).
  const w = openPrintWindow();
  if (!w) {
    return { ok: false, error: 'popup' };
  }

  try {
    const body: Record<string, unknown> = {
      page: { page: 1, size: 10000 },
      store,
      startDate: orderStartDate,
      endDate: orderEndDate,
      unprintedOnly: true,
    };
    if (deliveryStartDate) body.deliveryStartDate = deliveryStartDate;
    if (deliveryEndDate) body.deliveryEndDate = deliveryEndDate;

    const res = await fetch(apiUrl + 'mongol/orders', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      w.close();
      return { ok: false, error: 'network' };
    }
    const resJson: any = await res.json();
    let list = (resJson.data || []).filter((o: any) => (Number(o?.receiptCount) || 0) === 0);
    list = list.filter((o: any) => o.status === 'CONFIRMED');
    if (list.length === 0) {
      w.close();
      window.alert('Хэвлээгүй баталгаажсан захиалга олдсонгүй.');
      return { ok: false, error: 'empty' };
    }
    const orderIds = list.map((o: any) => Number(o.id)).filter((id: number) => id > 0);
    const aggregateRows = aggregateProductRows(list);
    const totalQty = totalPieces(aggregateRows);

    const deliveryReceiptSettings = await fetchDeliveryReceiptSettings(apiUrl);
    const bodyChunks = list.map((ord: any) => buildDeliveryReceiptPageHtml(ord, deliveryReceiptSettings));

    const footerRows = aggregateRows
      .map(
        (r) =>
          `<div class="agg-row"><span class="agg-name">${escapeHtml(r.name)}</span><span class="agg-qty">${r.count}</span></div>`
      )
      .join('');

    const bodyHtml = `${bodyChunks.join('\n')}
<div class="aggregate-footer">
  <h3>Нийт бараа (бүх баримт)</h3>
  ${footerRows}
  <div class="aggregate-total"><span>Нийт ширхэг</span><span>${totalQty}</span></div>
</div>`;

    return await new Promise<PrintUnprintedDeliveryReceiptBatchResult>((resolve) => {
      // Increment receipt counts only once the print dialog has closed.
      writeAndPrint(w, { title: 'Хүргэлтийн баримтууд', bodyHtml }, () => {
        fetch(apiUrl + 'mongol/order/increment-receipt-batch', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Authorization': token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderIds }),
        })
          .then(() => {
            resolve({ ok: true, printedCount: list.length });
          })
          .catch(() => {
            resolve({ ok: false, error: 'increment' });
          });
      });
    });
  } catch {
    w.close();
    return { ok: false, error: 'network' };
  }
}
